// Shared 3D-configurator core, used by BOTH the internal admin setup and the
// public storefront viewer. Extracted from pages/Configurator.js so the two
// consumers stay in sync. Pure rendering/material logic — no business data.
import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { Center } from '@react-three/drei';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';

// Channels a model part can be mapped to.
export const CHANNELS = [
  { id: 'metal', label: 'Metal' },
  { id: 'wood', label: 'Wood' },
  { id: 'cane', label: 'Cane' },
  { id: 'fabric1', label: 'Fabric 1' },
  { id: 'fabric2', label: 'Fabric 2' },
  { id: 'fabric3', label: 'Fabric 3' },
  { id: 'ignore', label: 'Keep original' },
];
export const FABRIC_CHANNEL_IDS = ['fabric1', 'fabric2', 'fabric3'];
export const FINISH_CHANNEL_IDS = ['metal', 'wood', 'cane'];

// Default customer-facing labels per channel. Admin can override these per
// product via config.labels; the storefront falls back to these.
export const DEFAULT_CHANNEL_LABELS = {
  metal: 'Frame / Metal',
  wood: 'Wood',
  cane: 'Cane',
  fabric1: 'Fabric 1',
  fabric2: 'Fabric 2',
  fabric3: 'Fabric 3',
};

// Resolve the label to show for a channel: custom (non-empty) → default → id.
export const labelForChannel = (id, labels = {}) => {
  const custom = labels && typeof labels[id] === 'string' ? labels[id].trim() : '';
  if (custom) return custom;
  return DEFAULT_CHANNEL_LABELS[id] || id;
};

// Back-compat: a configurator may store EITHER the new `models` array (one entry
// per base option) OR the legacy single-model fields (modelUrl/format/mappings).
// Always read through this so old products keep working.
export const normalizeConfiguratorModels = (cfg) => {
  if (!cfg) return [];
  if (Array.isArray(cfg.models) && cfg.models.length > 0) {
    return cfg.models
      .filter((m) => m && m.url)
      .map((m) => ({
        url: m.url,
        format: m.format || detectModelFormat(m.url),
        label: m.label || '',
        mappings: m.mappings || {},
      }));
  }
  if (cfg.modelUrl) {
    return [{
      url: cfg.modelUrl,
      format: cfg.format || detectModelFormat(cfg.modelUrl),
      label: '',
      mappings: cfg.mappings || {},
    }];
  }
  return [];
};

export const detectModelFormat = (filename) => {
  const n = (filename || '').toLowerCase();
  if (n.endsWith('.fbx')) return 'fbx';
  if (n.endsWith('.glb')) return 'glb';
  if (n.endsWith('.gltf')) return 'gltf';
  if (n.endsWith('.obj')) return 'obj';
  return '';
};

// Normalize a material name into a stable key for mapping lookups.
export const matKey = (name) => (name || 'unnamed').toLowerCase().trim();

const METAL_PATTERNS = ['bacak', 'altin', 'metal', 'steel', 'chrome', 'brass', 'frame', 'leg'];
const FABRIC_PATTERNS = ['kumas', 'fabric', 'cloth', 'seat', 'cushion', 'upholster', 'textile'];
const WOOD_PATTERNS = ['weneer', 'veneer', 'wood', 'ahsap', 'oak', 'walnut'];
const CANE_PATTERNS = ['cane', 'rattan', 'wicker', 'mesh'];

// First-guess channel for a material based on its name / metalness.
export const autoClassifyChannel = (mat) => {
  const name = (mat.name || '').toLowerCase();
  if (METAL_PATTERNS.some((p) => name.includes(p))) return 'metal';
  if (CANE_PATTERNS.some((p) => name.includes(p))) return 'cane';
  if (WOOD_PATTERNS.some((p) => name.includes(p))) return 'wood';
  if (FABRIC_PATTERNS.some((p) => name.includes(p))) return 'fabric1';
  if (mat.metalness !== undefined && mat.metalness > 0.4) return 'metal';
  return 'ignore';
};

// Load a model from a remote URL (or blob URL). Auto-orients Z-up models and
// scales to ~2.5 units. Returns { scene, materials: [{ name, autoChannel }] }.
export const loadModelFromUrl = (url, format) =>
  new Promise((resolve, reject) => {
    let loader;
    if (format === 'fbx') loader = new FBXLoader();
    else if (format === 'glb' || format === 'gltf') loader = new GLTFLoader();
    else if (format === 'obj') loader = new OBJLoader();
    else return reject(new Error('Unsupported format'));

    loader.load(
      url,
      (result) => {
        const scene = result.scene || result;

        let box = new THREE.Box3().setFromObject(scene);
        const dims = new THREE.Vector3();
        box.getSize(dims);
        if (dims.z > dims.y * 1.3 && dims.z > dims.x * 0.7) {
          scene.rotation.x = -Math.PI / 2;
          scene.updateMatrixWorld(true);
          box = new THREE.Box3().setFromObject(scene);
          box.getSize(dims);
        }
        const maxDim = Math.max(dims.x, dims.y, dims.z);
        if (maxDim > 0) scene.scale.multiplyScalar(2.5 / maxDim);

        const materials = [];
        const seen = new Set();
        scene.traverse((child) => {
          if (!child.isMesh) return;
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach((mat) => {
            if (!mat || seen.has(mat.uuid)) return;
            seen.add(mat.uuid);
            materials.push({ name: mat.name || 'unnamed', autoChannel: autoClassifyChannel(mat) });
          });
        });
        // Spread multiple fabric materials across Fabric 1/2/3.
        const slots = FABRIC_CHANNEL_IDS;
        let fi = 0;
        materials.forEach((m) => {
          if (m.autoChannel === 'fabric1') {
            m.autoChannel = slots[Math.min(fi, slots.length - 1)];
            fi += 1;
          }
        });
        resolve({ scene, materials });
      },
      undefined,
      (err) => reject(err)
    );
  });

// Build a MeshStandardMaterial from a texture image URL (fabric swatch).
export const buildTextureMaterial = (url, { repeat = 3, roughness = 0.85, metalness = 0, name = 'tex' } = {}) => {
  const loader = new THREE.TextureLoader();
  loader.setCrossOrigin('anonymous');
  const tex = loader.load(url);
  // Mirrored wrap hides seams on non-seamless photographic swatches.
  tex.wrapS = THREE.MirroredRepeatWrapping;
  tex.wrapT = THREE.MirroredRepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.MeshStandardMaterial({ map: tex, color: 0xffffff, roughness, metalness, name });
  mat.userData._tex = tex;
  return mat;
};

// Build a MeshStandardMaterial from a solid hex color (finish swatch).
export const buildColorMaterial = (hex, { metalness = 0.5, roughness = 0.5, name = 'color' } = {}) => {
  let color = '#888888';
  if (typeof hex === 'string' && /^#?[0-9a-f]{3,8}$/i.test(hex.trim())) {
    color = hex.trim().startsWith('#') ? hex.trim() : `#${hex.trim()}`;
  }
  return new THREE.MeshStandardMaterial({ color, metalness, roughness, name });
};

// React component: renders the scene and re-applies channel materials to mesh
// parts whenever `mappings` or `channelMaterials` change. Original materials are
// cloned once so 'ignore' / unmapped parts keep their look.
export const LoadedModel = ({ scene, mappings, channelMaterials }) => {
  const { invalidate } = useThree();
  useEffect(() => {
    if (!scene) return;
    scene.traverse((child) => {
      if (!child.isMesh) return;
      child.castShadow = true;
      child.receiveShadow = true;
      if (!child.userData._origMats) {
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        child.userData._origMats = mats.map((m) => ({ material: m.clone(), key: matKey(m.name) }));
      }
      const newMats = child.userData._origMats.map((entry) => {
        const channel = mappings[entry.key] || 'ignore';
        if (channel === 'ignore') return entry.material;
        return channelMaterials[channel] || entry.material;
      });
      child.material = newMats.length === 1 ? newMats[0] : newMats;
    });
    invalidate();
  }, [scene, mappings, channelMaterials, invalidate]);

  if (!scene) return null;
  return (
    <Center>
      <primitive object={scene} />
    </Center>
  );
};
